import { useState } from "react";
import { useNavigate } from "react-router";
import * as Sentry from "@sentry/react";

import { makeRequest } from "../lib/request";
import { useUserStore } from "../store/userStore";

interface LoginResponse {
  status: boolean;
  token: string;
  roles: string[];
  message?: string;
}

interface LoginCredentials {
  email: string;
  password: string;
  twoFactor?: string;
}

export function useAuth() {
  const navigate = useNavigate();
  const user = useUserStore((state) => state.user);
  const setUser = useUserStore((state) => state.setUser);

  const accessToken = useUserStore((state) => state.accessToken);
  const impersonationToken = useUserStore((state) => state.impersonationToken);
  const setAccessToken = useUserStore((state) => state.setAccessToken);
  const setImpersonationToken = useUserStore(
    (state) => state.setImpersonationToken
  );
  const setRoles = useUserStore((state) => state.setRoles);

  const [authError, setAuthError] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState<boolean>(false);
  const [twoFactorRequired, setTwoFactorRequired] = useState<boolean>(false);

  const validateCredentials = ({
    email,
    password,
  }: LoginCredentials): string | null => {
    if (!email || !password) {
      return "Email and password are required.";
    }
    if (!email.includes("@")) {
      return "Please enter a valid email address.";
    }
    if (password.length < 6) {
      return "Password must be at least 6 characters long.";
    }
    return null;
  };

  const refreshUser = async () => {
    try {
      const { notifications, roles, picture } = await makeRequest(
        "private/notifications",
        {
          cache: "no-cache",
          headers: {
            Authorization: `Bearer ${impersonationToken || accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      const { user } = await makeRequest("private/self", {
        cache: "no-cache",
        headers: {
          Authorization: `Bearer ${impersonationToken || accessToken}`,
          "Content-Type": "application/json",
        },
      });

      setUser({
        id: user.id,
        animations: user.animations,
        dark: user.dark,
        nickname: user.nickname,
        experience: user.experience,
        level: user.level,
        notifications,
        roles,
        picture,
        onboarded: user.metadata.isOnboarded,
        premium: user.premium,
      });

      if (user.dark) {
        const theme = window.localStorage.getItem("theme");
        const existingClass = document.body.classList.contains("dark");

        if (!theme || theme !== "dark") {
          window.localStorage.setItem("theme", "dark");
        }

        if (!existingClass) {
          document.body.classList.add("dark");
        }
      } else {
        window.localStorage.removeItem("theme");
        document.body.classList.remove("dark");
      }

      setRoles(roles);
    } catch (e) {
      console.error(e);
      setAccessToken(null);
      setRoles([]);
      setUser(null);

      await new Promise((resolve) => setTimeout(resolve, 50));

      window.location.href = "/login";
    }
  };

  const login = async (email: string, password: string, twoFactor?: string) => {
    if (authLoading) return;

    const credentials = { email, password, twoFactor };
    const validationError = validateCredentials(credentials);

    if (validationError) {
      setAuthError(validationError);
      return;
    }

    setAuthError(null);
    setAuthLoading(true);

    const abortController = new AbortController();

    try {
      const response = (await makeRequest("auth/login", {
        method: "POST",
        body: JSON.stringify(credentials),
        headers: {
          "Content-Type": "application/json",
        },
        signal: abortController.signal,
      })) as LoginResponse;

      if (response.status) {
        setAccessToken(response.token);
        setRoles(response.roles);

        const { notifications, roles, picture } = await makeRequest(
          "private/notifications",
          {
            cache: "no-cache",
            headers: {
              Authorization: `Bearer ${response.token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const { user } = await makeRequest("private/self", {
          cache: "no-cache",
          headers: {
            Authorization: `Bearer ${response.token}`,
            "Content-Type": "application/json",
          },
        });

        setUser({
          id: user.id,
          animations: user.animations,
          dark: user.dark,
          nickname: user.nickname,
          experience: user.experience,
          level: user.level,
          notifications,
          roles,
          picture,
          onboarded: user.metadata.isOnboarded,
          premium: user.premium,
        });
        setRoles(roles);

        Sentry.setUser({
          id: user.id,
          username: user.nickname,
        });

        await new Promise((resolve) => setTimeout(resolve, 50));

        setAuthLoading(false);
        navigate("/");
      } else {
        if (response.message === "2fa-required") {
          setTwoFactorRequired(true);
        } else {
          setAuthError(response.message ?? "Invalid credentials.");
        }
      }
    } catch (error) {
      setAuthError(
        error instanceof Error ? error.message : "Network error occurred"
      );
    } finally {
      setAuthLoading(false);
    }

    return () => {
      abortController.abort();
    };
  };

  const logout = async () => {
    localStorage.removeItem("user-storage");

    setImpersonationToken(null);
    setAccessToken(null);
    setRoles([]);
    setAuthError(null);
    setUser(null);

    await new Promise((resolve) => setTimeout(resolve, 50));

    navigate("/login", { replace: true });
  };

  return {
    login,
    logout,
    authError,
    authLoading,
    isAuthenticated: !!user,
    twoFactorRequired,
    refreshUser,
  };
}
